import type { OutboundMessageTransform } from "@expert/gaia";

/**
 * Realtime tools that only exist on the Simplr (LogicBuilder) backend. `customer-insights`
 * combines the device-cues and location-cues proactive flows into one app with the same response
 * format, so it is requested only on messages bound for the Simplr socket, never GAIA.
 */
export const SIMPLR_ONLY_REALTIME_TOOLS: readonly string[] = Object.freeze(["customer-insights"]);

interface ContextProperty {
    field: string;
    value: string;
}

interface SessionContextMessage {
    action: "update-session-context";
    context: { properties: ContextProperty[] };
}

function isSessionContextMessage(message: unknown): message is SessionContextMessage {
    if (typeof message !== "object" || message === null) return false;
    const candidate = message as Partial<SessionContextMessage>;
    return candidate.action === "update-session-context" && Array.isArray(candidate.context?.properties);
}

function withSimplrOnlyTools(value: string): string {
    const tools = value.split(",").filter(Boolean);
    const missing = SIMPLR_ONLY_REALTIME_TOOLS.filter((tool) => !tools.includes(tool));
    return missing.length > 0 ? [...tools, ...missing].join(",") : value;
}

/**
 * Appends {@link SIMPLR_ONLY_REALTIME_TOOLS} to the `realtime_tools` property of an
 * `update-session-context` message. Every other message, including a session-context message that
 * does not set `realtime_tools` (e.g. the locale or summary updates), is returned unchanged.
 */
export const addSimplrRealtimeTools: OutboundMessageTransform = (message) => {
    if (!isSessionContextMessage(message)) return message;

    const { properties } = message.context;
    if (!properties.some((property) => property.field === "realtime_tools")) return message;

    const transformed = {
        ...message,
        context: {
            ...message.context,
            properties: properties.map((property) =>
                property.field === "realtime_tools"
                    ? { ...property, value: withSimplrOnlyTools(property.value) }
                    : property,
            ),
        },
    };
    return transformed as unknown as typeof message;
};
